import { Controller } from "@hotwired/stimulus"
import jsmediatags from "jsmediatags/dist/jsmediatags.min.js"
import { formatTime } from "../lib/format_time"

// Reads ID3v2 tags client-side from a track's public URL and populates
// title/artist/album/year/cover. Used on the dashboard Music Files index, the
// Playlist edit tracklist, and the public Playlist player tracklist.
//
// The file is fetched as a Blob first (rather than handing jsmediatags the URL
// directly) so tag reading goes through its BlobFileReader (FileReader.readAsArrayBuffer),
// which is binary-safe. jsmediatags' URL reader instead pulls bytes through an
// old charset=x-user-defined XHR hack that reliably mangles larger binary
// frames (like embedded cover art) in modern browsers even though small text
// frames come through fine.
export default class extends Controller {
  static targets = ["title", "artist", "album", "year", "cover", "duration"]
  static values = { url: String }

  connect() {
    if (!this.urlValue) return

    fetch(this.urlValue)
      .then(response => response.blob())
      .then(blob => {
        jsmediatags.read(blob, {
          onSuccess: ({ tags }) => this.applyTags(tags),
          onError: () => {}
        })
        this.readDuration(blob)
      })
      .catch(() => {})
  }

  readDuration(blob) {
    if (!this.hasDurationTarget) return

    const objectUrl = URL.createObjectURL(blob)
    const audio = new Audio()
    audio.preload = "metadata"
    audio.addEventListener("loadedmetadata", () => {
      this.durationTarget.textContent = formatTime(audio.duration)
      URL.revokeObjectURL(objectUrl)
    })
    audio.src = objectUrl
  }

  applyTags(tags) {
    if (this.hasTitleTarget && tags.title) {
      this.titleTarget.textContent = tags.title
      this.titleTarget.title = tags.title
    }
    if (this.hasArtistTarget && tags.artist) this.artistTarget.textContent = tags.artist
    if (this.hasAlbumTarget && tags.album) this.albumTarget.textContent = tags.album
    if (this.hasYearTarget && tags.year) this.yearTarget.textContent = tags.year

    if (this.hasCoverTarget && tags.picture) {
      this.coverTarget.src = this.pictureToDataUrl(tags.picture)
      this.coverTarget.classList.remove("d-none")
    }

    this.dispatch("loaded", { detail: { tags } })
  }

  pictureToDataUrl(picture) {
    const { data, format } = picture
    let binary = ""
    for (let i = 0; i < data.length; i++) {
      binary += String.fromCharCode(data[i])
    }
    return `data:${format};base64,${window.btoa(binary)}`
  }
}
